import { z } from "zod";
import {
  DEAL_TYPES,
  HUBSPOT_PARTNER_DEAL_RELATIONSHIPS,
  type DealStatus,
  type DealType,
  type HubspotPartnerDealRelationship,
} from "../domain";

// Bucket 13 — admin deals. Replaces the 4 actions in
// /app/admin/deals/_actions.ts (linkDealAccount, unlinkDealAccount,
// addDealCollaborator, removeDealCollaborator).
//
// The legacy `dealCommentCreateSchema` + `dealStatusUpdateSchema` +
// `dealCreateSchema` already live in `../domain` and are re-exported via
// `@colab/shared`. This file adds the request-body schemas for the
// account-link and collaborator-add endpoints (bucket 13) and the partner-
// side rich-rebuild surface (Phase 7 sub-bucket 5).

export const dealAccountLinkSchema = z.object({
  hubspotCompanyId: z.string().trim().min(1),
});
export type DealAccountLinkInput = z.infer<typeof dealAccountLinkSchema>;

const vendorCollaboratorSchema = z.object({
  side: z.literal("vendor"),
  userId: z.string().uuid(),
});

const partnerCollaboratorSchema = z.object({
  side: z.literal("partner"),
  partnerUserId: z.string().uuid(),
});

export const dealCollaboratorAddSchema = z.discriminatedUnion("side", [
  vendorCollaboratorSchema,
  partnerCollaboratorSchema,
]);
export type DealCollaboratorAddInput = z.infer<typeof dealCollaboratorAddSchema>;

// ---------------------------------------------------------------------------
// Phase 7 sub-bucket 5 — partner-portal deals rich rebuild.
//
// The legacy `/app/portal/deals/page.tsx` is a single read-only list grouped
// by attribution relationship. The rich rebuild extends it with detail,
// submit, edit, withdraw, status timeline, comment-thread surfacing, and a
// HubSpot owner chip — per user decision recorded in
// ~/.claude/plans/continue-phase-7-curried-bachman.md.
//
// The portal exposes a *unified* view across two data sources:
//   1. `deals` rows  — partner-submitted leads pre-HubSpot acceptance,
//      EUR-only (no currency column), with internal status enum.
//   2. `hubspot_deal_attributions` + `hubspot_deals` — post-acceptance
//      attributions sourced from HubSpot, multi-currency.
//
// Discriminated by `source: 'deal' | 'attribution'`. Once a `deals` row
// is accepted and has a `hubspot_deal_id` populated, we prefer the
// attribution row and exclude the source `deals` row to avoid duplicates
// in the list.
// ---------------------------------------------------------------------------

export const dealSubmitRequestSchema = z.object({
  title: z.string().trim().min(1).max(200),
  deal_type: z.enum(DEAL_TYPES as readonly [DealType, ...DealType[]]),
  prospect_company: z.string().trim().min(1).max(200),
  prospect_contact_name: z
    .string()
    .trim()
    .max(200)
    .optional()
    .nullable()
    .transform((v) => v || null),
  prospect_contact_email: z
    .string()
    .trim()
    .max(320)
    .email()
    .optional()
    .nullable()
    .or(z.literal("").transform(() => null)),
  estimated_value_eur: z
    .number()
    .int()
    .min(0)
    .max(1_000_000_000)
    .optional()
    .nullable(),
  notes: z
    .string()
    .trim()
    .max(8000)
    .optional()
    .nullable()
    .transform((v) => v || null),
});
export type DealSubmitRequestInput = z.infer<typeof dealSubmitRequestSchema>;

export const dealUpdatePendingRequestSchema = dealSubmitRequestSchema.partial();
export type DealUpdatePendingRequestInput = z.infer<typeof dealUpdatePendingRequestSchema>;

// Discriminator: where the row came from.
export type PortalDealSource = "deal" | "attribution";

// Attribution direction shown on the partner-facing badge.
// referred_to_us → Inbound.
// referred_to_them + influenced → Outbound (sub-flavour stored as relationship).
// Partner-submitted `deals` rows derive direction from deal_type:
//   referral → inbound, co_sell → influenced, reseller → referred_to_them.
export type AttributionDirection = "inbound" | "outbound";

// Unified row shape returned by GET /api/portal/deals.
//
// Fields nullable wherever the source side may not provide them — e.g. a
// `deals` row has no `stage_id`/`stage_label`, an `attribution` row has no
// internal `status` or `notes`.
export type PortalDealRow = {
  /** Stable id: deals.id for source='deal', attributions.id for source='attribution'. */
  id: string;
  source: PortalDealSource;
  /** Display name. Pulled from deals.title or hubspot_deals.deal_name. */
  customer_name: string;
  /** Currency code. EUR for partner-submitted; hubspot_deals.deal_currency_code for attributions. */
  currency_code: string;
  /** Monetary value. NULL when unknown (legacy attributions can lack amount). */
  amount: number | null;
  /** Expected close date (attribution) OR null for partner-submitted (deals has no close-date column). */
  close_date: string | null;
  /** Last-touched timestamp — deals.updated_at OR hubspot_deals.pulled_at. */
  updated_at: string | null;
  /** Internal status — only present for source='deal'. */
  status: DealStatus | null;
  /** HubSpot stage id — only for source='attribution'. */
  stage_id: string | null;
  /** Human-readable stage label — only for source='attribution', resolved via getSalesPipelineStageLabels. */
  stage_label: string | null;
  /** Whether the row is in the "closed" half of the lifecycle. UI tab filter uses this. */
  is_closed: boolean;
  /** Withdrawal marker. true iff a deals row was withdrawn by the partner (soft state via status_history). */
  is_withdrawn: boolean;
  /** Attribution relationship — only for source='attribution'. */
  relationship: HubspotPartnerDealRelationship | null;
  /** Pre-classified direction. UI uses for the inbound/outbound aggregate split. */
  direction: AttributionDirection;
  /** Internal deal type — only for source='deal'. */
  deal_type: DealType | null;
  /** HubSpot deal id — populated for both: directly from attribution; from deals.hubspot_deal_id once accepted. */
  hubspot_deal_id: string | null;
  /** Deep link to the HubSpot deal record. Server-resolved (env-dependent); null when the deal isn't in HubSpot yet. */
  hubspot_url: string | null;
  /** True iff the partner can edit/withdraw: source='deal' AND status='submitted' AND submitted_by IS NOT NULL. */
  can_edit: boolean;
  /** True iff the deal was submitted by a partner user (vs system-imported). */
  is_partner_submitted: boolean;
};

// Detail row — extends PortalDealRow with detail-only fields.
export type PortalDealDetail = PortalDealRow & {
  /** Internal-status `deals` extras. NULL for attribution-only rows. */
  prospect_contact_name: string | null;
  prospect_contact_email: string | null;
  notes: string | null;
  /** HubSpot owner — Bookboost internal who collaborates with the partner. NULL for partner-submitted pre-HubSpot rows or when no owner is set. */
  hubspot_owner: PortalDealHubspotOwner | null;
};

export type PortalDealHubspotOwner = {
  hubspotOwnerId: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  /** Matched internal user, if HubSpot owner email maps to an internal profile. */
  matched: {
    userId: string;
    fullName: string | null;
    avatarUrl: string | null;
  } | null;
};

// Status timeline event — derived from deals.status_history JSON. Attribution
// rows return an empty array (HubSpot owns stage progression and we don't
// snapshot transitions).
export type DealTimelineEvent = {
  /** Status the deal transitioned TO. */
  status: DealStatus;
  /** Optional previous status (omitted on the initial INSERT entry). */
  from_status: DealStatus | null;
  /** ISO timestamp of the transition. */
  changed_at: string;
  /** auth.uid() of the actor at the time, OR 'system' for service-role / trigger writes. */
  changed_by: string;
  /** Free-form note — currently unused by the trigger; reserved for future per-event details. */
  note: string | null;
};

// Used by HUBSPOT_PARTNER_DEAL_RELATIONSHIPS at runtime; re-exported for the
// portal-deals UI to map direction → relationship-subflavour.
export const PORTAL_DEAL_RELATIONSHIPS = HUBSPOT_PARTNER_DEAL_RELATIONSHIPS;
